import React, { useState, useRef, useEffect, type ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../../../services/clipboard';
import type { CancelReasonOption } from '../../../services/types';
import { cancelReasonKey } from '../../../services/cancelReasons';

// ─── Tokens ──────────────────────────────────────────────────────────────────
export const LION_GREEN = '#6aaa3a';
export const LION_GREEN_LIGHT = 'rgb(248, 255, 239)';
export const LION_YELLOW_LIGHT = '#fdf6e3';
export const LION_RED_LIGHT = 'rgb(255, 239, 239)';
export const LION_GREY = '#8a8a8a';
export const LION_BORDER = '#e0e0e0';

// ─── Root wrapper — full viewport, scrollable when content overflows ──────────
export const LionRoot = styled.div`
  --btn-font-family: ;
  min-height: 100dvh;
  background: #fff;
  color: #333;
  font-family: 'PT Mono', monospace, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* vertical centering via auto margins on LionCard, not justify-content
     so that scroll works naturally when content exceeds viewport */
`;

// Centred container — margin: auto top/bottom pushes it to vertical centre
// when parent is taller than card; scrolls normally when card is taller
export const LionCard = styled.div`
  width: 100%;
  max-width: 550px;
  margin: auto 0;
  padding: 24px 12px 40px;
  box-sizing: border-box;
  text-align: center;
  font-weight: 400;
  line-height: 1.5;
`;

// ─── SVG Flags (emoji flags don't render on Windows) ─────────────────────────
const FLAG_STYLE = { borderRadius: 3, display: 'block', flexShrink: 0 } as const;
const f = (children: React.ReactNode) => (
  <svg width="22" height="15" viewBox="0 0 22 15" style={FLAG_STYLE}>
    {children}
  </svg>
);

const FlagRU = () => f(<><rect width="22" height="5" y="0" fill="#fff"/><rect width="22" height="5" y="5" fill="#0039A6"/><rect width="22" height="5" y="10" fill="#D52B1E"/></>);
const FlagEN = () => (
  <svg width="22" height="15" viewBox="0 0 44 30" style={FLAG_STYLE}>
    <rect width="44" height="30" fill="#012169"/>
    <path d="M0,0 L44,30 M44,0 L0,30" stroke="#fff" strokeWidth="6"/>
    <path d="M0,0 L44,30 M44,0 L0,30" stroke="#C8102E" strokeWidth="3.5"/>
    <path d="M0,15 H44" stroke="#fff" strokeWidth="10"/>
    <path d="M22,0 V30" stroke="#fff" strokeWidth="10"/>
    <path d="M0,15 H44" stroke="#C8102E" strokeWidth="6"/>
    <path d="M22,0 V30" stroke="#C8102E" strokeWidth="6"/>
  </svg>
);
const FlagUK = () => f(<><rect width="22" height="7" y="0" fill="#005BBB"/><rect width="22" height="8" y="7" fill="#FFD500"/></>);
const FlagDE = () => f(<><rect width="22" height="5" y="0" fill="#000"/><rect width="22" height="5" y="5" fill="#D00"/><rect width="22" height="5" y="10" fill="#FFCE00"/></>);
const FlagES = () => f(<><rect width="22" height="4" y="0" fill="#AA151B"/><rect width="22" height="7" y="4" fill="#F1BF00"/><rect width="22" height="4" y="11" fill="#AA151B"/></>);
const FlagAZ = () => f(<><rect width="22" height="5" y="0" fill="#0092BC"/><rect width="22" height="5" y="5" fill="#E8112D"/><rect width="22" height="5" y="10" fill="#00B140"/></>);
const FlagTR = () => f(<><rect width="22" height="15" fill="#E30A17"/><circle cx="9" cy="7.5" r="4" fill="#fff"/><circle cx="10.5" cy="7.5" r="3.2" fill="#E30A17"/><polygon points="14,7.5 15.5,6.5 15.5,8.5" fill="#fff"/></>);
const FlagKY = () => f(<><rect width="22" height="15" fill="#E8112D"/><circle cx="11" cy="7.5" r="4" fill="#F9CB39" fillOpacity="0.9"/></>);
const FlagID = () => f(<><rect width="22" height="7" y="0" fill="#CE1126"/><rect width="22" height="8" y="7" fill="#fff"/></>);
const FlagKK = () => f(<><rect width="22" height="15" fill="#00AFCA"/><circle cx="7" cy="7.5" r="3" fill="#FFE600" fillOpacity="0.9"/></>);
const FlagHI = () => f(<><rect width="22" height="5" y="0" fill="#FF9933"/><rect width="22" height="5" y="5" fill="#fff"/><rect width="22" height="5" y="10" fill="#138808"/><circle cx="11" cy="7.5" r="1.5" fill="#000080"/></>);
const FlagPT = () => f(<><rect width="22" height="15" fill="#009C3B"/><rect width="8" height="15" x="0" fill="#009C3B"/><polygon points="8,0 20,7.5 8,15" fill="#FEDF00"/><circle cx="14" cy="7.5" r="2.5" fill="#002776"/></>);
const FlagUZ = () => f(<><rect width="22" height="5" y="0" fill="#1EB53A"/><rect width="22" height="1" y="5" fill="#fff"/><rect width="22" height="3" y="6" fill="#fff"/><rect width="22" height="1" y="9" fill="#fff"/><rect width="22" height="5" y="10" fill="#CE1126"/></>);

// ─── Language Dropdown ────────────────────────────────────────────────────────
const LANGS = [
  { code: 'ru', Flag: FlagRU, label: 'Русский' },
  { code: 'en', Flag: FlagEN, label: 'English' },
  { code: 'uk', Flag: FlagUK, label: 'Українська' },
  { code: 'de', Flag: FlagDE, label: 'Deutsch' },
  { code: 'es', Flag: FlagES, label: 'Español' },
  { code: 'az', Flag: FlagAZ, label: 'Azərbaycan' },
  { code: 'tr', Flag: FlagTR, label: 'Türkçe' },
  { code: 'ky', Flag: FlagKY, label: 'Кыргызча' },
  { code: 'id', Flag: FlagID, label: 'Indonesia' },
  { code: 'kk', Flag: FlagKK, label: 'Қазақша' },
  { code: 'hi', Flag: FlagHI, label: 'हिन्दी' },
  { code: 'pt', Flag: FlagPT, label: 'Português' },
  { code: 'uz', Flag: FlagUZ, label: 'Oʻzbekcha' },
];

const LangWrapper = styled.div`
  position: relative;
  height: 40px;
  width: 70px;
`;

const LangTrigger = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background-color: rgb(247, 247, 247);
  color: #000;
  width: 100%;
  height: 100%;
  padding: 0 4px;
  font-size: .875rem;
  border-radius: 0.25rem;
  cursor: pointer;
  box-sizing: border-box;
  &::after {
    display: inline-block;
    flex-shrink: 0;
    margin-left: .255em;
    vertical-align: .255em;
    content: "";
    border-top: .3em solid;
    border-right: .3em solid transparent;
    border-bottom: 0;
    border-left: .3em solid transparent;
  }
  &:hover { background-color: #ebebeb; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid ${LION_BORDER};
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  overflow: hidden;
  min-width: 130px;
  z-index: 100;
`;

const LangOption = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ $active }) => ($active ? LION_GREEN_LIGHT : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: 'PT Mono', monospace;
  font-size: 13px;
  color: ${({ $active }) => ($active ? LION_GREEN : '#333')};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  text-align: left;
  transition: background 0.1s;
  &:hover { background: ${LION_GREEN_LIGHT}; }
`;

const LangFlag = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const LangFlagImg = styled.span`
  display: flex;
  & svg {
    width: 40px;
    height: auto;
    border-radius: 4px;
    margin-right: 5px;
    box-shadow: 0 2px 10px #0000004d;
  }
`;

const LangSelector = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGS.find(l => l.code === i18n.language) ?? LANGS[0];

  return (
    <LangWrapper ref={ref}>
      <LangTrigger onClick={() => setOpen(v => !v)}>
        <LangFlagImg><current.Flag /></LangFlagImg>
      </LangTrigger>
      {open && (
        <Dropdown>
          {LANGS.map(({ code, Flag, label }) => (
            <LangOption
              key={code}
              $active={code === i18n.language}
              onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
            >
              <LangFlag><Flag /></LangFlag>
              {label}
            </LangOption>
          ))}
        </Dropdown>
      )}
    </LangWrapper>
  );
};

// ─── Header ──────────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  margin-top: 24px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: rgb(121, 121, 121);
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 30px;
  font-weight: 600;
`;

export const LionHeader = () => {
  const { t } = useTranslation();
  return (
    <HeaderRow>
      <Title>{t('header.title')}</Title>
      <LangSelector />
    </HeaderRow>
  );
};

// ─── Step Indicator ──────────────────────────────────────────────────────────
type StepStatus = 'done' | 'active' | 'pending';

const StepsRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-evenly;
  gap: 16px;
  margin-bottom: 24px;
  margin-top: 8px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StepInner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 60px;
  align-items: center;
`;

const StepCircle = styled.div<{ $status: StepStatus }>`
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: rgb(92, 138, 30);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgb(212, 239, 175);
  font-weight: 600;
  font-family: 'PT Mono', monospace, sans-serif;
  transition: all 0.3s ease;
  ${({ $status }) => $status === 'done'    && `background-color: rgb(247, 247, 247); color: rgb(92, 138, 30);`}
  ${({ $status }) => $status === 'pending' && `background-color: rgb(247, 247, 247); color: #ccc;`}
`;

const StepLabel = styled.div<{ $status: StepStatus }>`
  margin: 8px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  font-size: 11px;
  text-align: center;
  max-width: 80px;
  color: ${({ $status }) => ($status === 'active' ? 'rgb(92, 138, 30)' : 'rgb(179, 179, 179)')};
`;

const StepConnector = styled.div`
  margin: 16px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: rgb(195, 195, 195);
  font-size: 9px;
  max-height: 10px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const CheckIcon = ({ width = 18, height = 13, stroke = '#C3C3C3' }: { width?: number; height?: number; stroke?: string }) => (
  <svg width={width} height={height} viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6.528L6.528 11.056L15.6 2" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const LionSteps = ({ step }: { step: 1 | 2 | 3 }) => {
  const { t } = useTranslation();
  const getStatus = (n: number): StepStatus => {
    if (n < step) return 'done';
    if (n === step) return 'active';
    return 'pending';
  };
  const steps = [
    { n: 1, label: t('steps.payByRequisites') },
    { n: 2, label: t('steps.uploadReceipt') },
    { n: 3, label: t('steps.processPayment') },
  ];
  return (
    <StepsRow>
      {steps.map((s, idx) => (
        <React.Fragment key={s.n}>
          <StepItem>
            <StepInner>
              <StepCircle $status={getStatus(s.n)}>
                {getStatus(s.n) === 'done' ? <CheckIcon /> : s.n}
              </StepCircle>
              <StepLabel $status={getStatus(s.n)}>{s.label}</StepLabel>
            </StepInner>
          </StepItem>
          {idx < steps.length - 1 && (
            <StepConnector>- - - - - - -</StepConnector>
          )}
        </React.Fragment>
      ))}
    </StepsRow>
  );
};

// ─── Bottom info ──────────────────────────────────────────────────────────────
const BottomArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimerPill = styled.div`
  margin: 50px 0 20px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  display: inline-block;
  font-size: 15px;
  color: rgb(100, 100, 100);
  background-color: rgb(247, 247, 247);
  width: fit-content;
  text-align: center;
  padding: 10px 25px;
  border-radius: 25px;
  font-family: 'PT Mono', monospace, sans-serif;
`;

const TimerValue = styled.span`
  color: rgb(92, 138, 30);
  font-family: PT Sans, sans-serif;
`;

const OrderNumber = styled.div`
  font-size: 14px;
  color: rgb(92, 138, 30);
  font-family: PT Sans, sans-serif;
  text-align: center;
  margin-bottom: 25px;
`;

export const LionBottom = ({ timeLeft, uuid }: { timeLeft: string; uuid?: string }) => {
  const { t } = useTranslation();
  return (
    <BottomArea>
      <TimerPill>
        {t('common.awaitingTransfer')} <TimerValue>{timeLeft}</TimerValue>
      </TimerPill>
      {uuid && <OrderNumber>{t('common.orderNumber')} {uuid}</OrderNumber>}
    </BottomArea>
  );
};

// ─── Shared icons ─────────────────────────────────────────────────────────────
export const ClipIcon = ({ size = 20, color = '#fff' }: { size?: number; color?: string }) => (
  <svg height={size} width={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path fill={color} d="M454.821,253.582L273.256,435.14c-11.697,11.697-25.124,20.411-39.484,26.235c-21.529,8.729-45.165,10.928-67.755,6.55c-22.597-4.378-44.054-15.25-61.597-32.784c-11.69-11.69-20.396-25.118-26.227-39.484c-8.729-21.529-10.929-45.165-6.55-67.748c4.386-22.597,15.25-44.055,32.778-61.596l203.13-203.13c7.141-7.134,15.299-12.43,24.035-15.969c13.1-5.318,27.516-6.656,41.263-3.994c13.769,2.677,26.798,9.27,37.498,19.963c7.133,7.134,12.423,15.292,15.968,24.035c5.318,13.092,6.657,27.502,3.987,41.264c-2.67,13.762-9.262,26.783-19.955,37.498L213.261,363.064c-2.534,2.528-5.375,4.364-8.436,5.61c-4.571,1.851-9.661,2.335-14.495,1.396c-4.848-0.954-9.355-3.225-13.15-7.006c-2.534-2.534-4.364-5.368-5.603-8.429c-1.865-4.571-2.342-9.668-1.402-14.495c0.947-4.841,3.225-9.355,7.005-13.149l175.521-175.528l-29.616-29.617l-175.528,175.52c-6.536,6.536-11.505,14.182-14.801,22.313c-4.941,12.195-6.166,25.473-3.702,38.202c2.449,12.73,8.686,24.989,18.503,34.799c6.543,6.55,14.182,11.519,22.305,14.809c12.202,4.948,25.473,6.165,38.21,3.702c12.722-2.449,24.989-8.678,34.806-18.511L439.97,195.602c11.142-11.149,19.571-24.113,25.167-37.917c8.394-20.717,10.48-43.314,6.294-64.971c-4.179-21.643-14.73-42.432-31.46-59.155c-11.149-11.142-24.114-19.571-37.918-25.166c-20.717-8.401-43.314-10.48-64.971-6.301c-21.643,4.186-42.431,14.737-59.155,31.468L74.803,236.695c-15.713,15.691-27.552,33.931-35.426,53.352c-11.817,29.154-14.765,60.97-8.863,91.462c5.888,30.478,20.717,59.696,44.29,83.254c15.698,15.713,33.931,27.552,53.36,35.426c29.146,11.811,60.97,14.758,91.455,8.863c30.478-5.895,59.696-20.717,83.254-44.29l181.566-181.564L454.821,253.582z"/>
  </svg>
);

// ─── Upload button (single shared component with MUI ripple) ─────────────────
const muiRippleIn = keyframes`
  from { transform: translate(-50%, -50%) scale(0); opacity: 0.35; }
  to   { transform: translate(-50%, -50%) scale(1); opacity: 0.35; }
`;
const muiRippleOut = keyframes`
  from { opacity: 0.35; }
  to   { opacity: 0; }
`;

const UploadBtnEl = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  outline: 0;
  margin: 0 0 16px;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  text-decoration: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  padding: 6px 16px;
  color: #fff;
  width: 100%;
  text-transform: none;
  background-color: rgb(147, 200, 74);
  font-size: 15px;
  box-shadow: none;
  transition: opacity 0.5s;
  height: 42px;
  border-radius: 5px;
  border: 1.5px solid transparent;
  max-width: 750px;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  &:hover { opacity: 0.85; }
`;

const UploadRippleEl = styled.span<{ $x: number; $y: number; $size: number; $out: boolean }>`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  width:  ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  left: ${({ $x }) => $x}px;
  top:  ${({ $y }) => $y}px;
  background: rgba(255, 255, 255, 0.45);
  animation: ${({ $out }) => ($out ? muiRippleOut : muiRippleIn)}
             ${({ $out }) => ($out ? '0.4s' : '0.5s')} ease forwards;
`;

const UploadBtnIconWrap = styled.span`
  display: inherit;
  margin-right: 8px;
  margin-left: -4px;
  font-size: 20px;
`;

const UploadBtnText = styled.span`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: white;
  font-weight: 600;
`;

interface UploadRipple { id: number; x: number; y: number; size: number; out: boolean; }

export const LionUploadBtn = ({ label, onClick }: { label: string; onClick?: () => void }) => {
  const [ripples, setRipples] = useState<UploadRipple[]>([]);
  const activeId = useRef<number | null>(null);
  const idRef = useRef(0);

  const onDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = 2 * Math.max(
      Math.hypot(x, y),
      Math.hypot(rect.width - x, y),
      Math.hypot(x, rect.height - y),
      Math.hypot(rect.width - x, rect.height - y),
    );
    const id = ++idRef.current;
    activeId.current = id;
    setRipples(prev => [...prev, { id, x, y, size, out: false }]);
  };

  const onUp = () => {
    const id = activeId.current;
    if (id === null) return;
    activeId.current = null;
    setRipples(prev => prev.map(r => r.id === id ? { ...r, out: true } : r));
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 450);
  };

  return (
    <UploadBtnEl
      type="button"
      onClick={onClick}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onMouseLeave={onUp}
    >
      {ripples.map(r => (
        <UploadRippleEl key={r.id} $x={r.x} $y={r.y} $size={r.size} $out={r.out} />
      ))}
      <UploadBtnIconWrap><ClipIcon size={25} color="#fff" /></UploadBtnIconWrap>
      <UploadBtnText>{label}</UploadBtnText>
    </UploadBtnEl>
  );
};

// ─── Card variants ────────────────────────────────────────────────────────────
export const GreenCard = styled.div`
  background: ${LION_GREEN_LIGHT};
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const YellowCard = styled.div`
  background: ${LION_YELLOW_LIGHT};
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const RedCard = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding-top: 56px;
  padding-bottom: 56px;
  background-color: rgb(255, 239, 239);
  border-radius: 20px;
  margin-top: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

export const GreyCard = styled.div`
  background: #f7f7f7;
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 12px;
`;

// ─── Button wrapper ───────────────────────────────────────────────────────────
export const LionButtonWrap = styled.div`
  margin-bottom: 1rem !important;
  width: 100%;
  & > button {
    margin-bottom: 0;
    font-family: PT Sans, sans-serif;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const btnBase = `
  display: inline-block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  min-height: 38px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  margin-bottom: 8px;
  box-sizing: border-box;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
  &:active { opacity: 0.85; }
`;

export const LionButtonGreen = styled.button`
  ${btnBase}
  background: ${LION_GREEN};
  color: #fff;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

export const LionButtonGrey = styled.button`
  ${btnBase}
  font-family: var(--btn-font-family);
  background: rgb(247, 247, 247);
  color: #000;
  &:hover { background: #e8e8e8; }
`;

// ─── Info row ─────────────────────────────────────────────────────────────────
export const InfoRow = styled.div`
  width: 100%;
  padding: 12px 24px;
  text-align: start;
  background-color: rgb(247, 247, 247);
  color: rgb(54, 54, 54);
  font-size: 14px;
  font-family: 'PT Mono', monospace, sans-serif;
  border-radius: 20px;
  margin-top: 16px;
  margin-bottom: 1rem !important;
  box-sizing: border-box;
`;

// ─── Copy row ─────────────────────────────────────────────────────────────────
const CopyRowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  background-color: ${LION_GREEN_LIGHT};
  border-radius: 40px;
  padding: 8px 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const CopyText = styled.div`
  margin: 0 0 0 0;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: black;
  width: 100%;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 14px;
  text-align: left;
`;

const CopyLabel = styled.div`
  font-size: 11px;
  color: ${LION_GREY};
  font-family: 'PT Mono', monospace;
`;

const CopyBtn = styled.button<{ $copied: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${({ $copied }) => ($copied ? 1 : 0.6)};
  transition: opacity 0.2s, transform 0.1s;
  &:active { transform: scale(0.9); }
`;

const CopyIconSvg = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6654 8.6026V11.4026C10.6654 13.7359 9.73203 14.6693 7.3987 14.6693H4.5987C2.26536 14.6693 1.33203 13.7359 1.33203 11.4026V8.6026C1.33203 6.26927 2.26536 5.33594 4.5987 5.33594H7.3987C9.73203 5.33594 10.6654 6.26927 10.6654 8.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.6654 4.6026V7.4026C14.6654 9.73594 13.732 10.6693 11.3987 10.6693H10.6654V8.6026C10.6654 6.26927 9.73203 5.33594 7.3987 5.33594H5.33203V4.6026C5.33203 2.26927 6.26536 1.33594 8.5987 1.33594H11.3987C13.732 1.33594 14.6654 2.26927 14.6654 4.6026Z" stroke="#171717" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CopyDoneSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10l4 4 8-8" stroke="#6aaa3a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const LionCopyRow = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(value).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <CopyRowWrapper>
      <CopyText>
        <CopyLabel>{label}</CopyLabel>
        {value}
      </CopyText>
      <CopyBtn $copied={copied} onClick={handleCopy}>
        {copied ? <CopyDoneSvg /> : <CopyIconSvg />}
      </CopyBtn>
    </CopyRowWrapper>
  );
};

// ─── Success row ──────────────────────────────────────────────────────────────
export const SuccessRow = styled.div`
  background: ${LION_GREEN_LIGHT};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: ${LION_GREEN};
`;

// ─── Spinner — SVG arc "run and chase" ───────────────────────────────────────
// r=20 → circumference ≈ 126px
// Two independent animations:
//   svgRotate  — whole SVG spins continuously
//   arcDash    — the arc oscillates between ~15% and ~85% of circumference
//                leading edge stretches ahead, trailing edge catches up
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const svgRotate = keyframes`
  100% { transform: rotate(360deg); }
`;

// dashoffset trick:
//  0%   → tiny arc (≈15%)
//  50%  → large arc (≈85%), leading edge already far ahead
//  100% → large arc fully contracted back → same as start + 1 full rotation
const arcDash = keyframes`
  0%   { stroke-dasharray: 1,   126; stroke-dashoffset: 0;    }
  50%  { stroke-dasharray: 107, 126; stroke-dashoffset: -38;  }
  100% { stroke-dasharray: 107, 126; stroke-dashoffset: -126; }
`;

const SpinSvg = styled.svg<{ $size: number }>`
  width:  ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  animation: ${svgRotate} 1.6s linear infinite;
`;

const SpinCircle = styled.circle`
  fill: none;
  stroke: ${LION_GREEN};
  stroke-width: 4;
  stroke-linecap: round;
  animation: ${arcDash} 1.4s ease-in-out infinite;
`;

/** Standalone spinner — use where a simple loading indicator is needed */
export const LionSpinner = ({ size = 24 }: { size?: number }) => (
  <SpinSvg $size={size} viewBox="0 0 50 50">
    <SpinCircle cx="25" cy="25" r="20" />
  </SpinSvg>
);

/** Spinner ring wrapping content (icon/emoji in the center) */
export const LionSpinnerRing = ({
  size = 48,
  children,
}: {
  size?: number;
  children?: ReactNode;
}) => (
  <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <SpinSvg $size={size} viewBox="0 0 50 50" style={{ position: 'absolute', inset: 0 }}>
      <SpinCircle cx="25" cy="25" r="20" />
    </SpinSvg>
    <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </div>
);

/** @deprecated use LionSpinner */
export const Spinner = LionSpinner;

// ─── LionScreen — единая обёртка для всех экранов ───────────────────────────
export interface LionAction {
  label: ReactNode;
  onClick: () => void;
  variant?: 'green' | 'grey';
  confirmLeave?: boolean;
}

interface LionScreenProps {
  step?: 1 | 2 | 3;
  uuid?: string;
  timeLeft?: string;   // если передан — показывает таймер (LionBottom)
  actions?: LionAction[];
  footerSlot?: ReactNode; // для нестандартных раскладок кнопок
  children: ReactNode;
}

const LionOrderNum = ({ uuid }: { uuid: string }) => {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', fontSize: 14, color: 'rgb(92, 138, 30)', fontFamily: 'PT Sans, sans-serif', marginBottom: 25 }}>
      {t('common.orderNumber')} {uuid}
    </div>
  );
};

// ─── Leave-confirm modal ──────────────────────────────────────────────────────
const LCOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const LCBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  max-width: calc(100vw - 32px);
  background-color: #fff;
  border-radius: 16px;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 11px 15px -7px,
    rgba(0, 0, 0, 0.14) 0px 24px 38px 3px,
    rgba(0, 0, 0, 0.12) 0px 9px 46px 8px;
  padding: 32px;
  white-space: pre-wrap;
  box-sizing: border-box;
`;

const LCText = styled.p`
  margin: 16px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  text-align: left;
`;

const LCBtnRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  margin-left: -8px;
`;

// CPAYMENT-1730: у варианта «уже оплатил» три исхода — в строку они не помещаются.
const LCBtnColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: 24px;
`;

const lcBtnBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  appearance: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  text-transform: none;
  outline: 0;
  border: 0;
  margin: 0;
  text-decoration: none;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1),
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              color 250ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const LCBtnLeave = styled.button`
  ${lcBtnBase}
  color: red;
  &:hover { background-color: rgba(255, 0, 0, 0.04); }
`;

const LCBtnCancel = styled.button`
  ${lcBtnBase}
  color: rgb(25, 118, 210);
  &:hover { background-color: rgba(25, 118, 210, 0.04); }
`;

// The cancel survey: optional, quieter than the buttons, so the decision still reads first.
const LionReasonHint = styled.p`
  margin: 16px 0 8px;
  padding-top: 12px;
  border-top: 1px solid #eeeeee;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #9a9a9a;
  text-align: center;
`;

const LionReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const LionReasonChip = styled.button<{ $picked: boolean }>`
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid ${({ $picked }) => ($picked ? LION_GREEN : '#e2e2e2')};
  background: ${({ $picked }) => ($picked ? LION_GREEN_LIGHT : '#fafafa')};
  color: ${({ $picked }) => ($picked ? LION_GREEN : '#666')};
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  cursor: pointer;
`;

const LionCancelSurvey = ({
  reasons,
  picked,
  onPick,
}: {
  reasons?: CancelReasonOption[];
  picked: CancelReasonOption | null;
  onPick: (reason: CancelReasonOption | null) => void;
}) => {
  const { t } = useTranslation();
  if (!reasons || reasons.length === 0) return null;

  return (
    <>
      <LionReasonHint>{t('cancelReason.hint')}</LionReasonHint>
      <LionReasonRow>
        {reasons.map((reason) => (
          <LionReasonChip
            key={reason.id}
            type='button'
            $picked={picked?.id === reason.id}
            data-sentry-element='onepage-cancel-reason'
            onClick={() => onPick(picked?.id === reason.id ? null : reason)}
          >
            {t(cancelReasonKey(reason.code))}
          </LionReasonChip>
        ))}
      </LionReasonRow>
    </>
  );
};

/**
 * CPAYMENT-1730: после «Я оплатил» уход со страницы перестаёт быть отменой заявки. Отмену не
 * отнимаем — клиент мог передумать и не платить, — но она становится отдельной кнопкой рядом с
 * возвратом к мерчанту, а не побочным эффектом кнопки «Покинуть страницу».
 */
export const LionLeaveModal = ({
  onLeave,
  onCancel,
  receiptIntent,
  onLeaveWithoutCancel,
  reasons,
}: {
  onLeave: (reason?: CancelReasonOption) => void;
  onCancel: () => void;
  receiptIntent?: boolean;
  onLeaveWithoutCancel?: () => void;
  reasons?: CancelReasonOption[];
}) => {
  const { t } = useTranslation();
  // The pick stays in the dialog until the client actually cancels; a second tap clears it.
  const [picked, setPicked] = useState<CancelReasonOption | null>(null);
  const leave = () => onLeave(picked ?? undefined);

  if (receiptIntent && onLeaveWithoutCancel) {
    return (
      <LCOverlay onClick={onCancel}>
        <LCBox onClick={e => e.stopPropagation()}>
          <LCText>
            {t('modal.leavePaidText', 'Вы отметили, что оплатили. Если перевод уже сделан — не отменяйте заявку: дождитесь проверки платежа или обратитесь в поддержку.\nЕсли вы передумали и деньги не переводили — отмените заявку.')}
          </LCText>
          <LionCancelSurvey reasons={reasons} picked={picked} onPick={setPicked} />
          <LCBtnColumn>
            <LCBtnCancel onClick={onLeaveWithoutCancel}>{t('modal.leaveToMerchant', 'Вернуться в сервис')}</LCBtnCancel>
            <LCBtnCancel onClick={onCancel}>{t('modal.stay', 'Остаться на странице')}</LCBtnCancel>
            <LCBtnLeave onClick={leave}>{t('modal.cancelTicket', 'Отменить заявку')}</LCBtnLeave>
          </LCBtnColumn>
        </LCBox>
      </LCOverlay>
    );
  }

  return (
    <LCOverlay onClick={onCancel}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCText>
          {t('modal.leaveText', "Пожалуйста дождитесь завершения оплаты.\nЕсли оплатили - нажмите кнопку 'Я оплатил'.\nЕсли уверены, что хотите покинуть страницу нажмите \"Покинуть страницу\"")}
        </LCText>
          <LionCancelSurvey reasons={reasons} picked={picked} onPick={setPicked} />
        <LCBtnRow>
          <LCBtnLeave onClick={leave}>{t('modal.leave', 'Покинуть страницу')}</LCBtnLeave>
          <LCBtnCancel onClick={onCancel}>{t('modal.cancel', 'Отмена')}</LCBtnCancel>
        </LCBtnRow>
      </LCBox>
    </LCOverlay>
  );
};

const LCTitle = styled.p`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  text-align: left;
`;

const LCBtnPrimary = styled.button`
  ${lcBtnBase}
  color: #fff;
  background-color: ${LION_GREEN};
  padding: 10px 16px;
  &:hover { background-color: #5c9430; }
`;

/**
 * CPAYMENT-1731: за 2 минуты до конца окна. Кнопка ведёт туда же, куда «Я оплатил» в флоу с
 * чеком: отметка «клиент платит» продлевает окно, страница открывает загрузку чека.
 */
export const LionLowTimeModal = ({
  onExtend,
  onDismiss,
}: {
  onExtend: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <LCOverlay onClick={onDismiss}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCTitle>{t('lowTime.title', 'Времени на оплату почти не осталось')}</LCTitle>
        <LCText>
          {t('lowTime.text', 'Если вы уже перевели деньги или переводите их сейчас — нажмите «Мне нужно ещё время», и мы продлим оплату, а затем попросим загрузить чек.')}
        </LCText>
        <LCBtnColumn>
          <LCBtnPrimary onClick={onExtend}>{t('lowTime.action', 'Мне нужно ещё время')}</LCBtnPrimary>
          <LCBtnCancel onClick={onDismiss}>{t('lowTime.dismiss', 'Закрыть')}</LCBtnCancel>
        </LCBtnColumn>
      </LCBox>
    </LCOverlay>
  );
};

export const LionScreen = ({ step, uuid, timeLeft, actions, footerSlot, children }: LionScreenProps) => {
  const hasConfirmLeave = actions?.some(a => a.confirmLeave) ?? false;

  useEffect(() => {
    if (!hasConfirmLeave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      (e as any).returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasConfirmLeave]);

  return (
    <LionRoot>
      <LionCard>
        <LionHeader />
        {step && <LionSteps step={step} />}
        {children}
        {footerSlot}
        {actions?.map((a, i) => (
          <LionButtonWrap key={i}>
            {a.variant === 'green'
              ? <LionButtonGreen onClick={a.onClick}>{a.label}</LionButtonGreen>
              : <LionButtonGrey onClick={a.onClick}>{a.label}</LionButtonGrey>
            }
          </LionButtonWrap>
        ))}
        {timeLeft
          ? <LionBottom timeLeft={timeLeft} uuid={uuid} />
          : uuid && <LionOrderNum uuid={uuid} />
        }
      </LionCard>
    </LionRoot>
  );
};
